/**
 * Roster: who is tracked, and how each of them is doing.
 *
 * The list doubles as the per-person summary, so the common question — "who is falling
 * behind?" — is answered without leaving the screen.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';

import { formatRate, type PersonTally } from '../../shared/stats.js';
import { addPeopleFromLines } from '../data/mutations.js';
import { useStore } from '../data/context.js';
import { useProjectDerived } from '../data/derived.js';
import { StateLegend } from '../ui/attendance.js';
import { StatusBar } from '../ui/charts/StatusBar.js';
import { plural } from '../ui/format.js';
import { ChevronRightIcon, PlusIcon } from '../ui/icons.js';
import { Button, Dialog, EmptyState, TextArea } from '../ui/primitives.js';
import { ProjectFrame } from '../ui/Shell.js';

/** How the roster is ordered. */
enum RosterOrder {
  Name = 'name',
  LowestRate = 'lowest-rate',
}

/** Sorts tallies for display; people with no recorded sessions sort last under rate order. */
function sortTallies(tallies: readonly PersonTally[], order: RosterOrder): PersonTally[] {
  const sorted = [...tallies];
  if (order === RosterOrder.Name) {
    return sorted.sort((left, right) => left.person.name.localeCompare(right.person.name, undefined, { sensitivity: 'base' }));
  }
  return sorted.sort((left, right) => {
    if (left.rate === null && right.rate === null) return 0;
    if (left.rate === null) return 1;
    if (right.rate === null) return -1;
    return left.rate - right.rate;
  });
}

/** Form for adding several people at once, one name per line. */
function AddPeopleDialog({ onClose, onAdd }: { onClose: () => void; onAdd: (block: string) => void }): ReactNode {
  const [block, setBlock] = useState('');
  const count = block.split('\n').filter((line) => line.trim() !== '').length;

  return (
    <Dialog
      title="Add people"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            tone="primary"
            disabled={count === 0}
            onClick={() => { onAdd(block); onClose(); }}
          >
            Add {count > 0 ? `${count} ${plural(count, 'person', 'people')}` : ''}
          </Button>
        </>
      }
    >
      <TextArea
        label="Names"
        value={block}
        onChange={setBlock}
        rows={8}
        placeholder={'Anna Becker\nBen Lorenz\nCarla Mensah'}
      />
    </Dialog>
  );
}

/** Renders the roster. */
export function PeoplePage(): ReactNode {
  const { projectId } = useParams();
  const store = useStore();
  const derived = useProjectDerived(projectId);
  const [order, setOrder] = useState(RosterOrder.Name);
  const [adding, setAdding] = useState(false);

  const tallies = useMemo(
    () => (derived === null ? [] : sortTallies(derived.personTallies, order)),
    [derived, order],
  );

  if (derived === null) {
    return <ProjectFrame><EmptyState message="This project is not on this device." /></ProjectFrame>;
  }

  const { project, people, pastSessions: sessions } = derived;

  const addButton = (
    <Button tone="primary" onClick={() => setAdding(true)}>
      <PlusIcon size={18} />
      Add people
    </Button>
  );

  return (
    <ProjectFrame>
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 pt-4 pb-2">
        <p className="text-tiny text-ink-2">
          Across {sessions.length} {plural(sessions.length, 'session', 'sessions')}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="h-11 rounded-control px-3 text-tiny font-semibold text-brand-text hover:bg-sunken"
            onClick={() => setOrder(order === RosterOrder.Name ? RosterOrder.LowestRate : RosterOrder.Name)}
          >
            {order === RosterOrder.Name ? 'Sort by lowest rate' : 'Sort by name'}
          </button>
          {addButton}
        </div>
      </div>

      {people.length === 0 ? (
        <EmptyState message="Nobody is on this roster yet. Add the names you want to check." action={addButton} />
      ) : (
        <>
        {/* The bars below carry no figures, so their colours are named once here. */}
        <div className="px-4 pb-3"><StateLegend /></div>
        <ul className="border-t border-line">
          {tallies.map((tally) => (
            <li key={tally.person.id} className="border-b border-line bg-surface">
              <Link
                to={`/p/${project.id}/people/${tally.person.id}`}
                className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-sunken sm:gap-4"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-row leading-snug font-medium">{tally.person.name}</p>
                  {tally.person.note === '' ? null : (
                    <p className="truncate text-tiny leading-snug text-ink-3">{tally.person.note}</p>
                  )}
                  {tally.missStreak >= 2 ? (
                    <p className="text-tiny leading-snug font-semibold text-missing-text">
                      {tally.missStreak} missed in a row
                    </p>
                  ) : null}
                </div>
                {/* One fixed column, so every person's distribution starts at the same x and
                    the roster can be read down rather than row by row. */}
                <div className="w-20 shrink-0 sm:w-40">
                  <StatusBar counts={tally.counts} height={6} />
                </div>
                <p className="tabular w-12 shrink-0 text-right text-row font-semibold">
                  {formatRate(tally.rate)}
                </p>
                <ChevronRightIcon size={18} className="shrink-0 text-ink-3" />
              </Link>
            </li>
          ))}
        </ul>
        </>
      )}

      {adding ? (
        <AddPeopleDialog
          onClose={() => setAdding(false)}
          onAdd={(block) => void addPeopleFromLines(store, project.id, block)}
        />
      ) : null}
    </ProjectFrame>
  );
}
